import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ClipboardList, Plus, Trash2, FileText, Search, X, Copy, ArrowLeft, RotateCcw,
} from 'lucide-react';
import { AppLayout } from '@/components/layout/AppLayout';
import { GuiaModuloFlutuante } from '@/components/shared/GuiaModuloFlutuante';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { formularioService } from '@/services/formulariosApi';
import { Formulario, FormularioStatus } from '@/types';
import { useAuth } from '@/contexts/AuthContext';

const STATUS_CONFIG: Record<FormularioStatus, { label: string; className: string }> = {
  rascunho: { label: 'Rascunho', className: 'bg-slate-200 text-slate-700' },
  publicado: { label: 'Publicado', className: 'bg-emerald-200 text-emerald-800' },
  arquivado: { label: 'Arquivado', className: 'bg-amber-200 text-amber-800' },
};

export default function GestaoAtividades() {
  const navigate = useNavigate();
  const { user, empresaFiltroId, empresasDisponiveis } = useAuth();
  const isAdmin = user?.tipo === 'admin' || user?.tipo === 'superadmin';
  const isSuperAdmin = user?.tipo === 'superadmin';

  const [formularios, setFormularios] = useState<Formulario[]>([]);
  const [loading, setLoading] = useState(true);
  const [verLixeira, setVerLixeira] = useState(false);
  const [novoOpen, setNovoOpen] = useState(false);
  const [novoTitulo, setNovoTitulo] = useState('');
  const [novaDescricao, setNovaDescricao] = useState('');
  const [novaEmpresaId, setNovaEmpresaId] = useState('');
  const [criando, setCriando] = useState(false);
  const [excluirAlvo, setExcluirAlvo] = useState<Formulario | null>(null);
  const [excluirPermanenteAlvo, setExcluirPermanenteAlvo] = useState<Formulario | null>(null);
  const [duplicandoId, setDuplicandoId] = useState<string | null>(null);
  const [filtroTitulo, setFiltroTitulo] = useState('');
  const [filtroStatus, setFiltroStatus] = useState('');
  const [ordenacao, setOrdenacao] = useState<'recente' | 'alfabetico' | 'respostas'>('recente');

  const load = async () => {
    setLoading(true);
    const res = await formularioService.getAll({
      ...(isSuperAdmin && empresaFiltroId ? { empresa_id: empresaFiltroId } : {}),
      lixeira: verLixeira,
    });
    if (res.success) setFormularios(res.data);
    else toast.error(res.error || 'Erro ao carregar formulários');
    setLoading(false);
  };

  useEffect(() => { load(); }, [empresaFiltroId, verLixeira]);

  const abrirNovoFormulario = () => {
    setNovoTitulo('');
    setNovaDescricao('');
    setNovaEmpresaId(isSuperAdmin ? (empresaFiltroId || '') : '');
    setNovoOpen(true);
  };

  const handleCriar = async () => {
    if (!novoTitulo.trim()) {
      toast.error('Informe um título');
      return;
    }
    if (isSuperAdmin && !novaEmpresaId) {
      toast.error('Selecione uma empresa');
      return;
    }
    setCriando(true);
    const res = await formularioService.create({
      titulo: novoTitulo.trim(),
      descricao: novaDescricao.trim() || null,
      ...(isSuperAdmin ? { empresa_id: novaEmpresaId } : {}),
    });
    setCriando(false);
    if (res.success) {
      toast.success('Formulário criado');
      setNovoOpen(false);
      setNovoTitulo('');
      setNovaDescricao('');
      navigate(`/formularios/${res.data.id}`);
    } else {
      toast.error(res.error || 'Erro ao criar formulário');
    }
  };

  const handleExcluir = async () => {
    if (!excluirAlvo) return;
    const res = await formularioService.delete(excluirAlvo.id);
    if (res.success) {
      toast.success('Formulário movido pra lixeira');
      setFormularios((prev) => prev.filter((f) => f.id !== excluirAlvo.id));
    } else {
      toast.error(res.error || 'Erro ao excluir formulário');
    }
    setExcluirAlvo(null);
  };

  const handleRestaurar = async (f: Formulario) => {
    const res = await formularioService.restaurar(f.id);
    if (res.success) {
      toast.success('Formulário restaurado');
      setFormularios((prev) => prev.filter((item) => item.id !== f.id));
    } else {
      toast.error(res.error || 'Erro ao restaurar formulário');
    }
  };

  const handleExcluirPermanente = async () => {
    if (!excluirPermanenteAlvo) return;
    const res = await formularioService.excluirPermanente(excluirPermanenteAlvo.id);
    if (res.success) {
      toast.success('Formulário excluído definitivamente');
      setFormularios((prev) => prev.filter((f) => f.id !== excluirPermanenteAlvo.id));
    } else {
      toast.error(res.error || 'Erro ao excluir formulário');
    }
    setExcluirPermanenteAlvo(null);
  };

  const handleDuplicar = async (f: Formulario) => {
    setDuplicandoId(f.id);
    const res = await formularioService.duplicar(f.id);
    setDuplicandoId(null);
    if (res.success) {
      toast.success('Formulário duplicado');
      navigate(`/formularios/${res.data.id}`);
    } else {
      toast.error(res.error || 'Erro ao duplicar formulário');
    }
  };

  const temFiltro = !!filtroTitulo || !!filtroStatus;
  const formulariosFiltrados = formularios
    .filter((f) => {
      const matchTitulo = !filtroTitulo || f.titulo.toLowerCase().includes(filtroTitulo.toLowerCase());
      const matchStatus = !filtroStatus || f.status === filtroStatus;
      return matchTitulo && matchStatus;
    })
    .sort((a, b) => {
      if (ordenacao === 'alfabetico') return a.titulo.localeCompare(b.titulo, 'pt-BR');
      if (ordenacao === 'respostas') return (b.total_respostas ?? 0) - (a.total_respostas ?? 0);
      return new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime();
    });

  return (
    <AppLayout>
      <div className="space-y-6">
        <div className="rounded-lg border bg-card p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-3">
            {verLixeira ? (
              <Button variant="ghost" size="icon" onClick={() => setVerLixeira(false)}>
                <ArrowLeft className="h-4 w-4" />
              </Button>
            ) : (
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <ClipboardList className="h-6 w-6" />
              </div>
            )}
            <div>
              <h1 className="text-2xl font-bold">{verLixeira ? 'Lixeira' : 'Formulários'}</h1>
              <p className="text-muted-foreground">
                {verLixeira
                  ? 'Formulários excluídos — restaure ou apague de vez'
                  : 'Crie formulários para acompanhar atividades, checklists e produtividade'}
              </p>
            </div>
          </div>
          {isAdmin && !verLixeira && (
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => setVerLixeira(true)}>
                <Trash2 className="h-4 w-4 mr-2" /> Lixeira
              </Button>
              <Button onClick={() => abrirNovoFormulario()}>
                <Plus className="h-4 w-4 mr-2" /> Novo formulário
              </Button>
            </div>
          )}
        </div>

        {!verLixeira && (
          <div className="flex flex-col gap-3 rounded-lg border bg-card p-3 sm:flex-row">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar por título..."
                value={filtroTitulo}
                onChange={(e) => setFiltroTitulo(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={filtroStatus || 'all'} onValueChange={(v) => setFiltroStatus(v === 'all' ? '' : v)}>
              <SelectTrigger className="sm:w-52">
                <SelectValue placeholder="Todos os status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os status</SelectItem>
                <SelectItem value="rascunho">Rascunho</SelectItem>
                <SelectItem value="publicado">Publicado</SelectItem>
                <SelectItem value="arquivado">Arquivado</SelectItem>
              </SelectContent>
            </Select>
            <Select value={ordenacao} onValueChange={(v) => setOrdenacao(v as typeof ordenacao)}>
              <SelectTrigger className="sm:w-52">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="recente">Mais recente</SelectItem>
                <SelectItem value="alfabetico">Alfabético</SelectItem>
                <SelectItem value="respostas">Mais respostas</SelectItem>
              </SelectContent>
            </Select>
            {temFiltro && (
              <Button variant="ghost" size="icon" onClick={() => { setFiltroTitulo(''); setFiltroStatus(''); }} title="Limpar filtros">
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        )}

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => <Skeleton key={i} className="h-36 rounded-lg" />)}
          </div>
        ) : formularios.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-muted-foreground">
              <FileText className="h-10 w-10 mx-auto mb-3 opacity-40" />
              <p>{verLixeira ? 'A lixeira está vazia.' : 'Nenhum formulário criado ainda.'}</p>
              {isAdmin && !verLixeira && (
                <Button variant="link" onClick={() => abrirNovoFormulario()}>Criar o primeiro formulário</Button>
              )}
            </CardContent>
          </Card>
        ) : verLixeira ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {formularios.map((f) => (
              <Card key={f.id} className="overflow-hidden">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base line-clamp-2">{f.titulo}</CardTitle>
                  {f.deletado_em && (
                    <CardDescription>Excluído em {new Date(f.deletado_em).toLocaleDateString('pt-BR')}</CardDescription>
                  )}
                </CardHeader>
                <CardContent className="flex items-center justify-between gap-2">
                  <Button variant="outline" size="sm" onClick={() => handleRestaurar(f)}>
                    <RotateCcw className="h-3.5 w-3.5 mr-1.5" /> Restaurar
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-muted-foreground hover:text-destructive"
                    onClick={() => setExcluirPermanenteAlvo(f)}
                  >
                    Excluir definitivamente
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : formulariosFiltrados.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-muted-foreground">
              <FileText className="h-10 w-10 mx-auto mb-3 opacity-40" />
              <p>Nenhum formulário encontrado para os filtros aplicados.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {formulariosFiltrados.map((f) => (
              <Card
                key={f.id}
                className="card-interactive cursor-pointer overflow-hidden"
                onClick={() => navigate(isAdmin ? `/formularios/${f.id}` : `/formularios/${f.id}/responder`)}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between gap-2">
                    <CardTitle className="text-base line-clamp-2">{f.titulo}</CardTitle>
                    {isAdmin && (
                      <div className="flex items-center gap-0.5 shrink-0">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-muted-foreground"
                          disabled={duplicandoId === f.id}
                          onClick={(e) => { e.stopPropagation(); handleDuplicar(f); }}
                          title="Duplicar"
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-muted-foreground hover:text-destructive"
                          onClick={(e) => { e.stopPropagation(); setExcluirAlvo(f); }}
                          title="Mover pra lixeira"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                  {f.descricao && <CardDescription className="line-clamp-2">{f.descricao}</CardDescription>}
                </CardHeader>
                <CardContent className="flex items-center justify-between text-sm text-muted-foreground">
                  <Badge className={STATUS_CONFIG[f.status].className}>{STATUS_CONFIG[f.status].label}</Badge>
                  <span>{f.total_campos ?? f.campos?.length ?? 0} campo(s) · {f.total_respostas ?? 0} resposta(s)</span>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      <Dialog open={novoOpen} onOpenChange={setNovoOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Novo formulário</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="novo-titulo">Título</Label>
              <Input id="novo-titulo" value={novoTitulo} onChange={(e) => setNovoTitulo(e.target.value)} placeholder="Ex: Caderno de turno" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="novo-descricao">Descrição (opcional)</Label>
              <Textarea id="novo-descricao" value={novaDescricao} onChange={(e) => setNovaDescricao(e.target.value)} rows={3} />
            </div>
            {isSuperAdmin && (
              <div className="space-y-2">
                <Label>Empresa</Label>
                <Select value={novaEmpresaId || undefined} onValueChange={setNovaEmpresaId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione uma empresa" />
                  </SelectTrigger>
                  <SelectContent>
                    {empresasDisponiveis.map((e) => (
                      <SelectItem key={e.id} value={e.id}>{e.nome}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNovoOpen(false)}>Cancelar</Button>
            <Button onClick={handleCriar} disabled={criando}>{criando ? 'Criando...' : 'Criar e continuar'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!excluirAlvo} onOpenChange={(open) => !open && setExcluirAlvo(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Mover pra lixeira?</AlertDialogTitle>
            <AlertDialogDescription>
              "{excluirAlvo?.titulo}" vai pra lixeira — dá pra restaurar depois, na tela de Lixeira.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleExcluir} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Mover pra lixeira
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!excluirPermanenteAlvo} onOpenChange={(open) => !open && setExcluirPermanenteAlvo(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir definitivamente?</AlertDialogTitle>
            <AlertDialogDescription>
              Isso vai excluir "{excluirPermanenteAlvo?.titulo}" e todas as respostas recebidas, de vez. Essa ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleExcluirPermanente} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Excluir definitivamente
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <GuiaModuloFlutuante imagemSrc="/guias-modulos/formularios.png" titulo="Formulários" />
    </AppLayout>
  );
}
